import React, { forwardRef, useId } from "react";
import { Listbox } from "@purpur/listbox";
import { type AutocompleteOption, useAutocomplete } from "@purpur/use-autocomplete";
import c from "classnames/bind";

import styles from "./search-field.module.scss";
import {
  type SearchFieldAutocompleteEnhancements,
  SearchFieldBase,
  type SearchFieldBaseProps,
} from "./search-field-base";

const cx = c.bind(styles);
const rootClassName = "purpur-search-field";

export type SearchFieldAutocompleteProps = SearchFieldBaseProps &
  Extract<SearchFieldAutocompleteEnhancements, { suggestions: AutocompleteOption[] }>;

export const SearchFieldAutocomplete = forwardRef<HTMLInputElement, SearchFieldAutocompleteProps>(
  (
    {
      suggestions,
      suggestionsLabel,
      onSelectSuggestion,
      openSuggestionsOnFocus,
      suggestionsMaxHeight,
      ...searchFieldProps
    },
    ref
  ) => {
    const randomId = useId();
    const inputId = (searchFieldProps.id as string | undefined) ?? randomId;

    const autocomplete = useAutocomplete({
      id: inputId,
      options: suggestions,
      listboxLabel: suggestionsLabel,
      disabled: searchFieldProps.disabled,
      readOnly: searchFieldProps.readOnly,
      openOnFocus: openSuggestionsOnFocus,
      listboxMaxHeight: suggestionsMaxHeight,
      onSelect: onSelectSuggestion,
      ["data-testid"]: searchFieldProps["data-testid"],
    });

    const setInputRef = (node: HTMLInputElement | null) => {
      (autocomplete.inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    };

    const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      searchFieldProps.onChange?.(event);
      if (!autocomplete.isOpen) {
        autocomplete.openListbox();
      }
    };

    return (
      <div
        ref={autocomplete.rootRef as React.RefObject<HTMLDivElement>}
        className={cx(`${rootClassName}__autocomplete-root`)}
      >
        <SearchFieldBase
          ref={setInputRef}
          {...searchFieldProps}
          onChange={handleInputChange}
          id={inputId}
          wrapperStyle={autocomplete.anchorStyle}
          {...autocomplete.inputProps}
        />
        {autocomplete.isOpen && (
          <Listbox {...autocomplete.listboxProps} className={cx(`${rootClassName}__listbox`)}>
            {suggestions.map((option, index) => {
              const { key, ...listboxItemProps } = autocomplete.getListboxItemProps(option, index);

              return (
                <Listbox.Item key={key} {...listboxItemProps}>
                  {option.label}
                </Listbox.Item>
              );
            })}
          </Listbox>
        )}
      </div>
    );
  }
);

SearchFieldAutocomplete.displayName = "SearchFieldAutocomplete";
